/**

 * Cuando el backend devuelve `master_data` en el JSON de una **acción** (no solo en GET_USER_ROLE_PROCEDURES_LIST),

 * hay que volcarlo en `userSession.procedures_list[]` del procedimiento correspondiente; si no, los diálogos que leen

 * `procedure.master_data` siguen mostrando datos de la carga inicial.

 *

 * Nota: `ApiFunctions.fetchApi` usa `response.text()` + `JSON.parse`, no `response.json()`, así que este hook sobre

 * `fetch` sigue viendo el mismo tráfico que el navegador y complementa a `refreshMasterData` en `@trazit/utils-cc-apis`.

 */



import * as procedureAwarenessApi from './procedure-awareness-api.js';

import { store, setUserSession } from '../../redux/store.js';



const PATCH_FLAG = '__trazitFetchMasterDataMergeInstalled';



function resolveProcInstanceName(data, urlStr) {

  const fromData =

    data.procInstanceName ??

    data.proc_instance_name ??

    data.procInstance ??

    null;

  if (fromData != null && String(fromData).trim()) return String(fromData).trim();



  try {

    const u = new URL(urlStr, typeof window !== 'undefined' ? window.location.origin : 'http://localhost');

    const q =

      u.searchParams.get('procInstanceName') ||

      u.searchParams.get('proc_instance_name');

    if (q && String(q).trim()) return decodeURIComponent(String(q).trim());

  } catch (_) {}



  try {

    const tab = sessionStorage.getItem('currentOpenTab') || '';

    const m = tab.match(/[?&]procName=([^&]+)/i);

    if (m) return decodeURIComponent(m[1]);

  } catch (_) {}



  try {

    const cov = JSON.parse(sessionStorage.getItem('currentOpenViewInfo') || '{}');

    const pin =

      cov?.selectedViewInfo?.procInstanceName ??

      cov?.tab?.procInstanceName ??

      cov?.viewModel?.procInstanceName;

    if (pin != null && String(pin).trim()) return String(pin).trim();

  } catch (_) {}



  return '';

}



function shouldAttemptMergeFromUrl(urlStr) {

  if (!urlStr || typeof urlStr !== 'string') return false;

  if (!/APIactions/i.test(urlStr)) return false;

  try {

    const u = new URL(urlStr, typeof window !== 'undefined' ? window.location.origin : 'http://localhost');

    const actionName = (u.searchParams.get('actionName') || '').trim();

    if (actionName === 'GET_USER_ROLE_PROCEDURES_LIST') return false;

    if (actionName === 'GET_PROCEDURE_AWARENESS_QUERIES') return false;

  } catch (_) {}

  return true;

}



/**

 * @param {Record<string, unknown>} data - Cuerpo JSON

 * @param {{ url?: string }} [meta]

 * @returns {boolean}

 */

export function applyMasterDataFromActionResponse(data, meta = {}) {

  const urlStr = meta.url || '';



  if (!data || typeof data !== 'object') return false;

  if (data.is_error === true || data.isError === true) return false;



  const md = data.master_data;

  if (md === undefined || md === null) return false;

  if (typeof md !== 'object' || Array.isArray(md)) return false;



  if (!shouldAttemptMergeFromUrl(urlStr)) return false;



  let raw;

  try {

    raw = sessionStorage.getItem('userSession');

    if (!raw) return false;

  } catch (_) {

    return false;

  }



  let session;

  try {

    session = JSON.parse(raw);

  } catch (_) {

    return false;

  }



  const procName = resolveProcInstanceName(data, urlStr);

  if (!procName) return false;



  const lk = procName.toLowerCase();

  const procedures = procedureAwarenessApi.getSessionProceduresArray(session);

  if (!Array.isArray(procedures)) return false;



  let hit = -1;

  for (let i = 0; i < procedures.length; i++) {

    const p = procedures[i];

    const pin = String(p?.procInstanceName ?? p?.proc_instance_name ?? '').trim();

    if (pin.toLowerCase() === lk) {

      hit = i;

      break;

    }

  }

  if (hit < 0) return false;



  const next = procedures.map((p, i) => (i === hit ? { ...p, master_data: md } : p));

  procedureAwarenessApi.setSessionProceduresArray(session, next);



  if (session.procedures_list) {

    session.procedures_list = procedureAwarenessApi.normalizeProceduresListShape(session.procedures_list);

  }



  try {

    sessionStorage.setItem('userSession', JSON.stringify(session));

  } catch (_) {

    return false;

  }



  store.dispatch(setUserSession(session));



  try {

    /**

     * No emitir `reLoadView`: en `procedures-controller` ese evento ejecuta `loadView()` →

     * `renderComponent()` con `innerHTML = ''`, se destruye el `<views-single-view>` y se pierden

     * filas expandidas / tablas hijas. La sesión ya está en sessionStorage y Redux; quien necesite

     * refrescar menús escucha `user-session-updated` (p. ej. `tr-default.loadProcedures`).

     */

    window.dispatchEvent(new CustomEvent('user-session-updated', { bubbles: true }));

  } catch (_) {}



  return true;

}



/** Una sola vez: tras cada fetch JSON a *APIactions*, fusionar `master_data` si viene en la respuesta. */

export function installMasterDataMergeFromActionResponses() {

  if (typeof window === 'undefined') return;

  if (window[PATCH_FLAG]) return;

  window[PATCH_FLAG] = true;



  const orig = window.fetch.bind(window);



  window.fetch = async function patchedFetch(input, init) {

    const urlStr =

      typeof input === 'string'

        ? input

        : input && typeof input.url === 'string'

          ? input.url

          : '';

    const response = await orig(input, init);



    if (!response.ok || !urlStr) return response;



    const ct = response.headers.get('content-type');

    if (!ct || !ct.includes('json')) return response;



    // Solo acciones: las queries no traen master_data mergeable; evitar clone+parse en cada GET.

    if (!shouldAttemptMergeFromUrl(urlStr)) return response;



    let data;

    try {

      data = await response.clone().json();

    } catch (_) {

      return response;

    }



    try {

      applyMasterDataFromActionResponse(data, { url: urlStr });

    } catch (e) {

      console.warn('[TRAZiT] applyMasterDataFromActionResponse', e);

    }



    return response;

  };

}


